export const isTauri = (): boolean => Boolean(window.__TAURI_INTERNALS__);

export type ClientPlatform = "web" | "windows" | "android";

export function getClientPlatform(): ClientPlatform {
  const declared = window.BianhuaClientPlatform;
  if (declared === "android" || declared === "windows" || declared === "web") return declared;
  return isTauri() ? "windows" : "web";
}

export const isAndroid = (): boolean => getClientPlatform() === "android";

/** Removes Windows' verbatim-path prefix before handing a path to the OS shell. */
export function localPathForDisplay(path: string): string {
  const value = String(path || "").trim();
  if (/^\\\\\?\\UNC\\/i.test(value)) return `\\\\${value.slice(8)}`;
  if (/^\\\\\?\\/.test(value)) return value.slice(4);
  return value;
}

export async function openLocalPath(path: string): Promise<void> {
  const value = localPathForDisplay(path);
  if (!value) throw new Error("本地文件路径为空");
  if (!isTauri()) throw new Error("网页端不能直接打开本机文件，请使用 Windows 或 Android 客户端");
  const { openPath } = await import("@tauri-apps/plugin-opener");
  await openPath(value);
}

export async function revealLocalPath(path: string): Promise<void> {
  const value = localPathForDisplay(path);
  if (!value) throw new Error("本地文件路径为空");
  if (!isTauri()) throw new Error("网页端不能直接打开本机目录，请使用 Windows 客户端");
  const { revealItemInDir } = await import("@tauri-apps/plugin-opener");
  await revealItemInDir(value);
}

export async function invokeNative<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isTauri()) throw new Error("当前不是原生客户端环境");
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(command, args);
}

/** @deprecated Prefer invokeNative for code shared by Windows and Android. */
export const invokeDesktop = invokeNative;

export const DESKTOP_CLOSE_CONFIRMATION = {
  title: "退出彼岸花·智体",
  message: "确定要退出客户端吗？未完成的服务器任务会继续运行，重新打开后可恢复。",
  okLabel: "退出客户端",
  cancelLabel: "取消",
} as const;

export async function confirmDesktopClose(
  ask: (message: string, options: {
    title: string;
    kind: "warning";
    okLabel: string;
    cancelLabel: string;
  }) => Promise<boolean>,
): Promise<boolean> {
  return ask(DESKTOP_CLOSE_CONFIRMATION.message, {
    title: DESKTOP_CLOSE_CONFIRMATION.title,
    kind: "warning",
    okLabel: DESKTOP_CLOSE_CONFIRMATION.okLabel,
    cancelLabel: DESKTOP_CLOSE_CONFIRMATION.cancelLabel,
  });
}

export async function completeDesktopClose(
  approved: boolean,
  destroy: () => Promise<void>,
): Promise<boolean> {
  if (!approved) return false;
  // Window.close() emits another close-requested event. Once the user has
  // explicitly approved exiting, destroy the single main window so the
  // interceptor cannot recursively block the confirmed close.
  await destroy();
  return true;
}

function confirmDesktopCloseInApp(): Promise<boolean> {
  return new Promise((resolve) => {
    document.querySelector<HTMLDialogElement>("#bianhua-close-confirm")?.remove();
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = document.createElement("dialog");
    dialog.id = "bianhua-close-confirm";
    dialog.className = "runtime-dialog";
    dialog.setAttribute("aria-labelledby", "bianhua-close-confirm-title");
    const card = document.createElement("div");
    card.className = "runtime-dialog-card";
    const eyebrow = document.createElement("span");
    eyebrow.className = "runtime-dialog-eyebrow";
    eyebrow.textContent = "彼岸花·智体";
    const title = document.createElement("h2");
    title.id = "bianhua-close-confirm-title";
    title.textContent = DESKTOP_CLOSE_CONFIRMATION.title;
    const message = document.createElement("p");
    message.textContent = DESKTOP_CLOSE_CONFIRMATION.message;
    const actions = document.createElement("div");
    actions.className = "runtime-dialog-actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "runtime-secondary";
    cancel.textContent = DESKTOP_CLOSE_CONFIRMATION.cancelLabel;
    const approve = document.createElement("button");
    approve.type = "button";
    approve.className = "runtime-primary";
    approve.textContent = DESKTOP_CLOSE_CONFIRMATION.okLabel;
    actions.append(cancel, approve);
    card.append(eyebrow, title, message, actions);
    dialog.append(card);
    let approved = false;
    approve.addEventListener("click", () => {
      approved = true;
      dialog.close();
    });
    cancel.addEventListener("click", () => dialog.close());
    dialog.addEventListener("cancel", () => {
      approved = false;
    });
    dialog.addEventListener("close", () => {
      dialog.remove();
      if (!approved && restoreTarget?.isConnected) queueMicrotask(() => restoreTarget.focus());
      resolve(approved);
    }, { once: true });
    document.body.append(dialog);
    dialog.showModal();
    cancel.focus();
  });
}

export async function bindWindowControls(): Promise<void> {
  const controls = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-window-action]"));
  const previewWindowsChrome = new URLSearchParams(location.search).get("platform") === "windows-preview";
  if (!isTauri()) {
    for (const control of controls) control.hidden = !previewWindowsChrome;
    document.documentElement.dataset.platform = previewWindowsChrome ? "windows-preview" : "web";
    return;
  }

  if (isAndroid()) {
    for (const control of controls) control.hidden = true;
    document.documentElement.dataset.platform = "android";
    return;
  }

  document.documentElement.dataset.platform = "windows";
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  const appWindow = getCurrentWindow();
  let closePromptOpen = false;
  const requestConfirmedClose = async (): Promise<void> => {
    if (closePromptOpen) return;
    closePromptOpen = true;
    try {
      let approved = false;
      try {
        const { confirm } = await import("@tauri-apps/plugin-dialog");
        approved = await confirmDesktopClose(confirm);
      } catch {
        approved = await confirmDesktopCloseInApp();
      }
      await completeDesktopClose(approved, () => appWindow.destroy());
    } finally {
      closePromptOpen = false;
    }
  };
  await appWindow.onCloseRequested((event) => {
    event.preventDefault();
    void requestConfirmedClose();
  });
  const titlebar = document.querySelector<HTMLElement>(".titlebar");
  titlebar?.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || (event.target as Element).closest("button, a, input, textarea, select, [role='tab']")) return;
    appWindow.startDragging().catch(() => {});
  });
  titlebar?.addEventListener("dblclick", (event) => {
    if ((event.target as Element).closest("button, a, input, textarea, select, [role='tab']")) return;
    appWindow.toggleMaximize().catch(() => {});
  });
  for (const control of controls) {
    control.addEventListener("click", async () => {
      const action = control.dataset.windowAction;
      if (action === "minimize") await appWindow.minimize();
      if (action === "maximize") await appWindow.toggleMaximize();
      if (action === "close") await requestConfirmedClose();
    });
  }
}
